import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, ShieldCheck, FileText, Users, Gift, Layers, Sparkles, Truck, Wrench, Eye } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { MachineCard } from "@/components/MachineCard";
import { EnquiryModal } from "@/components/EnquiryModal";

const HERO_IMG = "https://images.unsplash.com/photo-1720036236694-d0a231c52563?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const DISCOVER = [
  { to: "/new-machines", icon: Sparkles, title: "NEW MACHINES", text: "Explore new machines from manufacturers, dealers and suppliers.", cta: "EXPLORE NEW MACHINES", accent: "brand" },
  { to: "/used-machines", icon: Truck, title: "USED MACHINES", text: "Find used, old and second-hand machines from sellers across India.", cta: "EXPLORE USED MACHINES", accent: "orange" },
  { to: "/spare-parts", icon: Wrench, title: "SPARE PARTS", text: "Discover machine spare parts, components and accessories.", cta: "EXPLORE SPARE PARTS", accent: "charcoal" },
];

const WHY = [
  { icon: Layers, title: "Machines & Spare Parts Focused", text: "A specialized platform focused on machinery and related spare parts." },
  { icon: Sparkles, title: "New & Used Options", text: "Explore both new machines and pre-owned equipment." },
  { icon: FileText, title: "Detailed Product Information", text: "Photos, specifications, descriptions and videos." },
  { icon: Users, title: "Direct Seller Connection", text: "Connect directly with the seller." },
  { icon: Gift, title: "Buyer Enquiries are FREE", text: "No buyer registration or enquiry fee." },
];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [featuredNew, setFeaturedNew] = useState([]);
  const [featuredUsed, setFeaturedUsed] = useState([]);
  const [featuredSpare, setFeaturedSpare] = useState([]);
  const [recent, setRecent] = useState([]);
  const [popular, setPopular] = useState([]);
  const [q, setQ] = useState("");
  const [enquireMachine, setEnquireMachine] = useState(null);
  const [visitorsToday, setVisitorsToday] = useState(null);
  const navigate = useNavigate();

  const loadFeatured = (type, setter) =>
    api.get("/machines/featured", { params: { listing_type: type } })
      .then((r) => (r.data.length ? setter(r.data) : api.get("/machines/recent", { params: { listing_type: type } }).then((rr) => setter(rr.data))))
      .catch(() => {});

  useEffect(() => {
    api.get("/visitors/today").then((r) => setVisitorsToday(r.data.today)).catch(() => {});
    api.get("/machines/popular").then((r) => setPopular(r.data)).catch(() => {});
    api.get("/categories", { params: { type: "machine" } }).then((r) => setCategories(r.data)).catch(() => {});
    loadFeatured("new", setFeaturedNew);
    loadFeatured("used", setFeaturedUsed);
    loadFeatured("spare", setFeaturedSpare);
    Promise.all([
      api.get("/machines/recent", { params: { listing_type: "new" } }).then((r) => r.data).catch(() => []),
      api.get("/machines/recent", { params: { listing_type: "used" } }).then((r) => r.data).catch(() => []),
      api.get("/machines/recent", { params: { listing_type: "spare" } }).then((r) => r.data).catch(() => []),
    ]).then((groups) => {
      const merged = [].concat(...groups).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 12);
      setRecent(merged);
    });
  }, []);

  const search = (e) => {
    e.preventDefault();
    navigate(`/used-machines?search=${encodeURIComponent(q)}`);
  };
  const onEnquire = useCallback((m) => setEnquireMachine(m), []);

  const Section = ({ eyebrow, title, viewAll, viewTestid, children }) => (
    <div className="mb-8 flex items-end justify-between">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{eyebrow}</p>
        <h2 className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">{title}</h2>
      </div>
      {viewAll && (
        <Link to={viewAll} data-testid={viewTestid} className="hidden text-sm font-semibold text-[hsl(var(--brand))] sm:inline-flex sm:items-center sm:gap-1">
          View all <ArrowRight size={14} />
        </Link>
      )}
      {children}
    </div>
  );

  return (
    <Layout>
      <Seo
        title="Mashiniya — New & Used Machines and Spare Parts | Indra Sphere"
        description="Explore new machines, used machines, second-hand equipment and machine spare parts on Mashiniya. Browse construction, agricultural, drilling, industrial and earthmoving machinery and connect directly with sellers."
        canonical={window.location.origin + "/"}
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-[hsl(var(--charcoal))]">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--charcoal))] via-[hsl(var(--charcoal))]/85 to-[hsl(var(--charcoal))]/40" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-orange">Mashiniya by Indra Sphere</p>
          <h1 className="max-w-3xl font-display text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
            FIND THE COMPLETE MACHINE OR <span className="text-orange">SPARE PART</span>
          </h1>
          <p className="mt-5 max-w-xl text-lg text-slate-300">
            Explore new machines, used machines and spare parts and connect directly with the seller.
          </p>

          <form onSubmit={search} className="mt-8 flex max-w-2xl items-center gap-2 rounded-full bg-white p-2 shadow-2xl">
            <Search className="ml-3 shrink-0 text-slate-400" size={20} />
            <input
              data-testid="hero-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search machine, brand, model or spare part..."
              className="w-full bg-transparent px-1 text-sm text-slate-900 outline-none sm:text-base"
            />
            <button data-testid="hero-search-btn" type="submit" className="shrink-0 rounded-full bg-brand px-5 py-3 text-sm font-bold text-white sm:px-8">SEARCH</button>
          </form>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/used-machines" data-testid="hero-browse-btn" className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-bold text-white">
              BROWSE LISTINGS <ArrowRight size={16} />
            </Link>
            <Link to="/sell-your-machine" data-testid="hero-sell-btn" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 text-sm font-bold text-white hover:bg-white/10">
              LIST YOUR PRODUCT
            </Link>
          </div>

          {visitorsToday !== null && (
            <div data-testid="visitor-counter" className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm">
              <Eye size={15} className="text-orange" />
              <span>{visitorsToday} {visitorsToday === 1 ? "person has" : "people have"} visited today</span>
            </div>
          )}

        </div>
      </section>

      {/* WHAT ARE YOU LOOKING FOR */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="mb-8 text-center font-display text-2xl font-bold text-slate-900 sm:text-3xl">WHAT ARE YOU LOOKING FOR?</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {DISCOVER.map((d) => {
            const bar = d.accent === "orange" ? "bg-[hsl(var(--action))]" : d.accent === "charcoal" ? "bg-[hsl(var(--charcoal))]" : "bg-[hsl(var(--brand))]";
            const iconc = d.accent === "orange" ? "text-orange" : d.accent === "charcoal" ? "text-[hsl(var(--charcoal))]" : "text-brand";
            return (
              <Link key={d.to} to={d.to} data-testid={`discover-${d.accent}`} className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:shadow-xl">
                <div className={`h-1.5 w-full ${bar}`} />
                <div className="flex flex-1 flex-col p-7">
                  <d.icon className={`mb-4 ${iconc}`} size={34} />
                  <h3 className="font-display text-xl font-extrabold text-slate-900">{d.title}</h3>
                  <p className="mt-2 flex-1 text-sm text-slate-500">{d.text}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[hsl(var(--brand))]">
                    {d.cta} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* MOST VIEWED MACHINES */}
      {popular.length > 0 && (
        <section data-testid="popular-section" className="bg-slate-50 py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Section eyebrow="Trending now" title="Most Viewed Machines" />
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {popular.slice(0, 8).map((m) => <MachineCard key={m.id} m={m} onEnquire={onEnquire} />)}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED NEW MACHINES */}
      {featuredNew.length > 0 && (
        <section className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Section eyebrow="Brand-new stock" title="Featured New Machines" viewAll="/new-machines" viewTestid="home-view-new" />
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {featuredNew.slice(0, 8).map((m) => <MachineCard key={m.id} m={m} onEnquire={onEnquire} />)}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED USED MACHINES */}
      {featuredUsed.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <Section eyebrow="Hand-picked pre-owned" title="Featured Used Machines" viewAll="/used-machines" viewTestid="home-view-used" />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {featuredUsed.slice(0, 8).map((m) => <MachineCard key={m.id} m={m} onEnquire={onEnquire} />)}
          </div>
        </section>
      )}

      {/* BROWSE BY CATEGORY */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Section eyebrow="Browse by type" title="Browse by Machine Category" viewAll="/categories" viewTestid="home-view-cats" />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {categories.map((c) => (
              <Link key={c.id} to={`/used-machines/${c.slug}`} data-testid={`category-tile-${c.slug}`} className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="aspect-square overflow-hidden bg-slate-100">
                  {c.image_url && <img src={fileUrl(c.image_url)} alt={c.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />}
                </div>
                <div className="p-3">
                  <h3 className="line-clamp-2 font-display text-sm font-bold leading-tight text-slate-900">{c.name}</h3>
                  <p className="mt-1 text-xs font-semibold text-[hsl(var(--brand))]">{c.machine_count} Machines</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED SPARE PARTS */}
      {featuredSpare.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <Section eyebrow="Keep machines running" title="Featured Spare Parts" viewAll="/spare-parts" viewTestid="home-view-spare" />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {featuredSpare.slice(0, 8).map((m) => <MachineCard key={m.id} m={m} onEnquire={onEnquire} />)}
          </div>
        </section>
      )}

      {/* RECENTLY ADDED */}
      {recent.length > 0 && (
        <section className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Section eyebrow="Fresh listings" title="Recently Added" />
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {recent.map((m) => <MachineCard key={m.id} m={m} onEnquire={onEnquire} />)}
            </div>
          </div>
        </section>
      )}

      {/* WHY */}
      <section className="bg-[hsl(var(--charcoal))] py-16 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-10 font-display text-2xl font-bold sm:text-3xl">WHY MASHINIYA?</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {WHY.map((w) => (
              <div key={w.title} className="rounded-xl border border-white/10 bg-white/5 p-5">
                <w.icon className="mb-3 text-orange" size={26} />
                <h3 className="font-display text-base font-bold">{w.title}</h3>
                <p className="mt-1.5 text-sm text-slate-400">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIST YOUR PRODUCT CTA */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center gap-5 rounded-3xl border border-slate-200 bg-white p-10 text-center">
          <h2 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Have a machine or spare part to list?</h2>
          <p className="max-w-xl text-slate-500">Share your product details with us and our team will contact you. Listing on Mashiniya is simple.</p>
          <Link to="/sell-your-machine" data-testid="home-list-cta" className="rounded-full bg-brand px-8 py-3 text-sm font-bold text-white">LIST YOUR PRODUCT</Link>
        </div>
      </section>

      <EnquiryModal machine={enquireMachine} open={!!enquireMachine} onClose={() => setEnquireMachine(null)} />
    </Layout>
  );
}
